import { useState, useEffect } from 'react';
import { toast } from '../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { useOnboarding, useReminders, useAuth } from '../stores';
import { saveUserProfile } from '../services/firebase';
import { analytics } from '../services/analytics';
import {
  OnboardingLayout,
  WelcomeStep,
  ProfileStep,
  ProgramsStep,
  SobrietyStep,
  SponsorStep,
  NotificationsStep,
  TourStep,
  CompletionStep,
} from '../components/features/onboarding';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const {
    hasCompletedOnboarding,
    currentStep,
    currentStepIndex,
    nextStep,
    previousStep,
    data,
    setProfile,
    setSelectedPrograms,
    addSobrietyDate,
    removeSobrietyDate,
    setSponsorInfo,
    setNotifications,
    setTourCompleted,
    markAsCompleted,
  } = useOnboarding();

  const { markOnboardingCompleted, markSponsorSkipped } = useReminders();

  // Escape hatch: if hasCompletedOnboarding becomes true while on this page
  // (e.g. Firestore sync resolved after a redirect-flow race condition on a new device),
  // redirect immediately to dashboard instead of showing the onboarding flow again.
  useEffect(() => {
    if (hasCompletedOnboarding) {
      navigate('/dashboard', { replace: true });
    }
  }, [hasCompletedOnboarding, navigate]);

  const handleFinishOnboarding = async () => {
    if (!user?.uid || !user?.email) {
      console.error('Cannot save: User not authenticated');
      toast.error('Authentication error', 'Please try logging in again.');
      return;
    }

    setIsSaving(true);

    try {
      // Save all onboarding data to Firestore
      await saveUserProfile(user.uid, user.email, data);
      console.log('✅ Onboarding data saved successfully');

      // Mark onboarding as completed for reminder system
      markOnboardingCompleted();

      // Track if user skipped sponsor (has sponsor but didn't add code)
      const skippedSponsor = data.hasSponsor === false || (data.hasSponsor === true && !data.sponsorInviteCode);
      markSponsorSkipped(skippedSponsor);

      // Mark onboarding as completed - data persists in local store for app usage
      markAsCompleted();

      // Track onboarding completion and trial start
      analytics.onboardingCompleted(data.selectedPrograms);
      analytics.trialStarted();

      console.log('✅ Onboarding completed - data preserved in store:', {
        selectedPrograms: data.selectedPrograms,
        customPrograms: data.customPrograms,
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Error saving onboarding data:', error);
      toast.error('Failed to save profile', 'Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSobrietySubmit = (dates: Parameters<typeof addSobrietyDate>[0][]) => {
    // Clear existing and add all new
    data.sobrietyDates.forEach((_, i) => removeSobrietyDate(i));
    dates.forEach((date) => addSobrietyDate(date));
    nextStep();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep onContinue={nextStep} />;

      case 'profile':
        return (
          <ProfileStep
            initialData={data.profile}
            onSubmit={(profile) => {
              setProfile(profile);
              nextStep();
            }}
          />
        );

      case 'programs':
        return (
          <ProgramsStep
            selectedPrograms={data.selectedPrograms}
            onSubmit={(programs) => {
              setSelectedPrograms(programs);
              nextStep();
            }}
          />
        );

      case 'sobriety':
        return (
          <SobrietyStep
            selectedPrograms={data.selectedPrograms}
            sobrietyDates={data.sobrietyDates}
            onSubmit={handleSobrietySubmit}
          />
        );

      case 'sponsor':
        return (
          <SponsorStep
            hasSponsor={data.hasSponsor}
            sponsorInviteCode={data.sponsorInviteCode}
            onSubmit={(hasSponsor, inviteCode) => {
              setSponsorInfo(hasSponsor, inviteCode);
              nextStep();
            }}
          />
        );

      case 'notifications':
        return (
          <NotificationsStep
            initialData={data.notifications}
            onSubmit={(notifications) => {
              setNotifications(notifications);
              nextStep();
            }}
          />
        );

      case 'tour':
        return (
          <TourStep
            onComplete={() => {
              setTourCompleted(true);
              nextStep();
            }}
          />
        );

      case 'completion':
        return (
          <CompletionStep
            firstName={data.profile?.firstName || 'Friend'}
            onFinish={handleFinishOnboarding}
            isLoading={isSaving}
          />
        );

      default:
        return null;
    }
  };

  return (
    <OnboardingLayout
      currentStep={currentStep}
      currentStepIndex={currentStepIndex}
      onBack={previousStep}
      showBackButton={currentStepIndex > 0 && currentStep !== 'completion'}
    >
      {renderStep()}
    </OnboardingLayout>
  );
}
